import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaChevronDown, FaChevronUp, FaTag } from 'react-icons/fa';
import { useAuth } from '../auth/AuthProvider';
import { useSubscription } from '../hooks/useSubscription';
import supabase from '../config/supabaseClient';
import UpgradeModal from './UpgradeModal';

const brand = {
  primary: '#29458d',
  secondary: '#dbc078',
  primaryLight: '#e8edf7',
  white: '#ffffff',
  gray50: '#f8f9fa',
  gray100: '#e9ecef',
  gray400: '#adb5bd',
  gray600: '#6c757d',
  gray800: '#343a40',
  green: '#16a34a',
};

const FREE_FEATURES = [
  { label: 'Time Estimate Calculator', included: true },
  { label: 'Save horse & run data', included: true },
  { label: 'Download results', included: true },
  { label: 'Text (SMS) race reminders', included: false },
  { label: 'Scheduled text (SMS) messages', included: false },
  { label: 'Race day text (SMS) summary', included: false },
];

const PREMIUM_FEATURES = [
  { label: 'Time Estimate Calculator', included: true },
  { label: 'Save horse & run data', included: true },
  { label: 'Download results', included: true },
  { label: 'Text (SMS) race reminders', included: true, note: 'while active / trial' },
  { label: 'Scheduled text (SMS) messages', included: true, note: 'while active / trial' },
  { label: 'Race day text (SMS) summary', included: true, note: 'while active / trial' },
];

const FAQS = [
  {
    q: 'What happens when I cancel?',
    a: 'Your SMS features stay active until the end of your current billing period. After that, your account drops back to the free experience — you keep the calculator, all saved horse and run data, and downloads forever. Nothing is deleted.',
  },
  {
    q: 'Will I be charged during the free trial?',
    a: 'No. The 30-day free trial begins when you sign up for SMS. Billing only starts after the trial ends, and only if you haven\'t cancelled. You\'ll receive a reminder email before the first charge.',
  },
  {
    q: 'Can I switch from Free to Premium later?',
    a: 'Yes — you can upgrade any time from your Profile page. Your saved data carries over automatically.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'All major credit and debit cards (Visa, Mastercard, American Express, Discover) processed securely through Stripe.',
  },
  {
    q: 'Is my payment information secure?',
    a: 'Yes. Payments are processed by Stripe, which is PCI-DSS Level 1 certified. We never see or store your full card details on our servers.',
  },
  {
    q: 'Does Pre Go Prep charge sales tax?',
    a: 'Pre Go Prep applies sales tax.',
  },
];

const FeatureRow = ({ feature }) => (
  <li
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.625rem',
      padding: '0.5rem 0',
      color: feature.included ? brand.gray800 : brand.gray400,
      fontSize: '0.95rem',
    }}
  >
    {feature.included ? (
      <FaCheck style={{ color: brand.green, fontSize: '0.85rem', flexShrink: 0, marginTop: '0.3rem' }} />
    ) : (
      <span
        style={{
          width: '0.85rem',
          height: '2px',
          backgroundColor: brand.gray400,
          flexShrink: 0,
          display: 'inline-block',
          marginTop: '0.65rem',
        }}
      />
    )}
    <span style={{ textDecoration: feature.included ? 'none' : 'line-through' }}>
      {feature.label}
      {feature.note && (
        <span style={{ display: 'block', fontSize: '0.78rem', color: brand.gray600, fontWeight: 400 }}>
          {feature.note}
        </span>
      )}
    </span>
  </li>
);

const PricingCard = ({
  title,
  price,
  priceUnit,
  trialNote,
  trialDisclaimer,
  billingFrequency,
  features,
  ctaLabel,
  ctaSubCopy,
  onCta,
  popular = false,
  showSubscribedBadge = false,
  hideCta = false,
}) => (
  <div
    style={{
      flex: '1 1 320px',
      maxWidth: '420px',
      minWidth: '280px',
      backgroundColor: brand.white,
      borderRadius: '12px',
      border: `2px solid ${popular ? brand.primary : brand.gray100}`,
      boxShadow: popular ? '0 12px 28px rgba(41, 69, 141, 0.15)' : '0 2px 6px rgba(0,0,0,0.04)',
      padding: '2rem 1.75rem',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {popular && (
      <div
        style={{
          position: 'absolute',
          top: '-14px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: brand.secondary,
          color: brand.primary,
          padding: '0.3rem 1rem',
          borderRadius: '999px',
          fontSize: '0.75rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        Most Popular
      </div>
    )}

    <h3
      style={{
        margin: 0,
        fontSize: '1.5rem',
        fontWeight: 700,
        color: brand.primary,
        textAlign: 'center',
      }}
    >
      {title}
    </h3>

    <div style={{ textAlign: 'center', margin: '1rem 0 0.25rem' }}>
      <span style={{ fontSize: '2.75rem', fontWeight: 800, color: brand.gray800 }}>{price}</span>
      {priceUnit && (
        <span style={{ fontSize: '1rem', color: brand.gray600, marginLeft: '0.25rem' }}>
          {priceUnit}
        </span>
      )}
    </div>

    {billingFrequency && (
      <p
        style={{
          textAlign: 'center',
          margin: 0,
          color: brand.gray600,
          fontSize: '0.85rem',
        }}
      >
        {billingFrequency}
      </p>
    )}

    {trialNote ? (
      <p
        style={{
          textAlign: 'center',
          margin: '0.5rem 0 0',
          color: brand.primary,
          fontWeight: 700,
          fontSize: '0.95rem',
        }}
      >
        {trialNote}
      </p>
    ) : null}

    {trialDisclaimer ? (
      <p
        style={{
          textAlign: 'center',
          margin: '0.25rem 0 1rem',
          color: brand.gray600,
          fontSize: '0.8rem',
          fontStyle: 'italic',
        }}
      >
        {trialDisclaimer}
      </p>
    ) : (
      <div style={{ height: '0.75rem' }} />
    )}

    <hr style={{ border: 'none', borderTop: `1px solid ${brand.gray100}`, margin: '0 0 1rem' }} />

    <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1 }}>
      {features.map((f) => (
        <FeatureRow key={f.label} feature={f} />
      ))}
    </ul>

    {showSubscribedBadge ? (
      <div
        style={{
          marginTop: '1.5rem',
          width: '100%',
          padding: '0.85rem 1rem',
          borderRadius: '8px',
          fontSize: '0.95rem',
          fontWeight: 600,
          textAlign: 'center',
          backgroundColor: brand.secondary,
          color: brand.primary,
          border: `1px solid ${brand.secondary}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
        }}
      >
        <FaCheck style={{ color: brand.primary }} /> You're already subscribed
      </div>
    ) : hideCta ? (
      <div style={{ marginTop: '1.5rem', height: '2.75rem' }} />
    ) : (
      <>
        {ctaSubCopy && (
          <p
            style={{
              margin: '1.25rem 0 0',
              textAlign: 'center',
              color: brand.gray600,
              fontSize: '0.8rem',
              lineHeight: 1.5,
            }}
          >
            {ctaSubCopy}
          </p>
        )}

        <button
          onClick={onCta}
          style={{
            marginTop: ctaSubCopy ? '0.75rem' : 'auto',
            width: '100%',
            padding: '0.85rem 1rem',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            border: 'none',
            backgroundColor: popular ? brand.secondary : brand.white,
            color: popular ? brand.primary : brand.primary,
            boxShadow: popular ? '0 4px 12px rgba(219, 192, 120, 0.4)' : 'none',
            outline: popular ? 'none' : `2px solid ${brand.primary}`,
            outlineOffset: '-2px',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {ctaLabel}
        </button>
      </>
    )}
  </div>
);

const FaqItem = ({ q, a, isOpen, onToggle }) => (
  <div
    style={{
      borderBottom: `1px solid ${brand.gray100}`,
      padding: '1rem 0',
    }}
  >
    <button
      onClick={onToggle}
      style={{
        width: '100%',
        background: 'none',
        border: 'none',
        padding: 0,
        textAlign: 'left',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '1rem',
      }}
    >
      <span style={{ color: brand.gray800, fontSize: '1rem', fontWeight: 600 }}>{q}</span>
      {isOpen ? (
        <FaChevronUp style={{ color: brand.primary, flexShrink: 0 }} />
      ) : (
        <FaChevronDown style={{ color: brand.gray600, flexShrink: 0 }} />
      )}
    </button>
    {isOpen && (
      <p
        style={{
          margin: '0.75rem 0 0',
          color: brand.gray600,
          lineHeight: 1.6,
          fontSize: '0.95rem',
        }}
      >
        {a}
      </p>
    )}
  </div>
);

const PricingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isActive: isSubscribed } = useSubscription();
  const [openFaq, setOpenFaq] = useState(null);
  const [trialBusy, setTrialBusy] = useState(false);
  const [trialError, setTrialError] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  // Derived state: which scenario are we in?
  const isLoggedOut = !user;
  const isLoggedInSubscribed = !!user && isSubscribed;

  // Premium card click for logged-in, non-subscribed users:
  // - has phone number → trigger Stripe Checkout directly
  // - no phone number → redirect to /profile so they can enter one first
  const handleStartTrial = async () => {
    if (!user) {
      navigate('/signup');
      return;
    }
    setTrialBusy(true);
    setTrialError(null);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('phone_number')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) throw error;

      const hasPhone = !!(data?.phone_number && String(data.phone_number).trim());
      if (!hasPhone) {
        navigate('/profile#sms-notifications', {
          state: { fromPricingTrial: true },
        });
        return;
      }

      // Open the standardized disclosure modal — it handles Stripe Checkout
      // internally after the user accepts the terms.
      setUpgradeModalOpen(true);
    } catch (err) {
      console.error('Start trial error:', err);
      setTrialError(err.message || 'Could not start trial. Please try again.');
    } finally {
      setTrialBusy(false);
    }
  };

  const handleGoToCalculator = () => navigate('/calculations');
  const handleCreateAccount = () => navigate('/signup');

  return (
    <div style={{ backgroundColor: brand.white, minHeight: '60vh' }}>
      {/* Hero */}
      <section
        style={{
          background: brand.primaryLight,
          padding: '3rem 1rem 2rem',
          textAlign: 'center',
        }}
      >
        <h1
          style={{
            margin: 0,
            color: brand.primary,
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.15,
          }}
        >
          Choose the plan that fits your race day
        </h1>
        <p
          style={{
            margin: '0.75rem auto 0',
            maxWidth: '560px',
            color: brand.gray600,
            fontSize: '1rem',
            lineHeight: 1.5,
          }}
        >
          Start free, or unlock SMS reminders with a 30-day free trial. Cancel anytime.
        </p>
      </section>

      {/* Subscribed banner (above cards) */}
      {isLoggedInSubscribed && (
        <section
          style={{
            backgroundColor: brand.secondary,
            borderTop: `1px solid ${brand.secondary}`,
            borderBottom: `1px solid ${brand.secondary}`,
            padding: '1rem',
            textAlign: 'center',
          }}
        >
          <p
            style={{
              margin: 0,
              color: brand.primary,
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            <FaCheck style={{ marginRight: '0.5rem', color: brand.primary }} />
            You're already subscribed — thanks for supporting Pre Go Prep!
          </p>
        </section>
      )}

      {/* Plan cards */}
      <section
        style={{
          padding: '2.5rem 1rem',
          maxWidth: '1000px',
          margin: '0 auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1.5rem',
            justifyContent: 'center',
            alignItems: 'stretch',
          }}
        >
          <PricingCard
            title="Core Plan"
            price="$0"
            priceUnit="No Subscription"
            features={FREE_FEATURES}
            ctaLabel="Go to Calculator"
            onCta={handleGoToCalculator}
            hideCta={isLoggedOut || isLoggedInSubscribed}
          />
          <PricingCard
            title="Premium Plan"
            price="$6.99"
            priceUnit="/ month"
            billingFrequency="Monthly"
            trialNote="30-day free trial"
            trialDisclaimer="30 days free, then $6.99/mo unless cancelled"
            features={PREMIUM_FEATURES}
            ctaLabel={trialBusy ? 'Starting…' : 'Start your free trial'}
            ctaSubCopy="30 days free. Billing starts after trial unless you cancel. Monthly billing only. Prices in USD."
            onCta={handleStartTrial}
            popular
            hideCta={isLoggedOut}
            showSubscribedBadge={isLoggedInSubscribed}
          />
        </div>

        {/* Unified CTA below cards (only for logged-out or subscribed states) */}
        {(isLoggedOut || isLoggedInSubscribed) && (
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <button
              onClick={isLoggedOut ? handleCreateAccount : handleGoToCalculator}
              style={{
                padding: '0.9rem 2.5rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                border: 'none',
                backgroundColor: brand.primary,
                color: brand.white,
                boxShadow: '0 4px 12px rgba(41, 69, 141, 0.25)',
                transition: 'transform 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {isLoggedOut ? 'Create account' : 'Go to Calculator'}
            </button>
          </div>
        )}

        {trialError && (
          <p
            style={{
              textAlign: 'center',
              marginTop: '1rem',
              color: '#dc2626',
              fontSize: '0.9rem',
            }}
          >
            {trialError}
          </p>
        )}
      </section>

      {/* Early Supporter callout */}
      <section
        style={{
          backgroundColor: brand.secondary,
          borderTop: `1px solid ${brand.secondary}`,
          borderBottom: `1px solid ${brand.secondary}`,
          padding: '1.5rem 1rem',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            maxWidth: '720px',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          <FaTag style={{ color: brand.primary, fontSize: '1.25rem' }} />
          <span style={{ color: brand.gray800, fontSize: '0.95rem', lineHeight: 1.5 }}>
            <strong style={{ color: brand.primary }}>Early Supporter Pricing:</strong>{' '}
            Accounts created before <strong>June 1, 2026</strong> receive a recurring discount
            at checkout — <strong style={{ color: brand.primary }}>$4.99/month</strong>.
          </span>
        </div>
      </section>

      {/* FAQ */}
      <section
        style={{
          maxWidth: '720px',
          margin: '0 auto',
          padding: '3rem 1rem',
        }}
      >
        <h2
          style={{
            margin: 0,
            marginBottom: '1.5rem',
            color: brand.primary,
            fontSize: '1.5rem',
            fontWeight: 700,
            textAlign: 'center',
          }}
        >
          Frequently asked questions
        </h2>
        {FAQS.map((faq, i) => (
          <FaqItem
            key={faq.q}
            q={faq.q}
            a={faq.a}
            isOpen={openFaq === i}
            onToggle={() => setOpenFaq(openFaq === i ? null : i)}
          />
        ))}
      </section>

      {/* Post-cancellation behavior */}
      <section
        style={{
          backgroundColor: brand.gray50,
          padding: '1.5rem 1rem',
          textAlign: 'center',
          borderTop: `1px solid ${brand.gray100}`,
        }}
      >
        <p
          style={{
            margin: 0,
            color: brand.gray800,
            fontSize: '0.95rem',
            lineHeight: 1.6,
            maxWidth: '720px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <strong style={{ color: brand.primary }}>If you cancel:</strong> SMS stops at the end of
          your billing period. Calculator, saved data, and downloads remain on the free experience.
        </p>
      </section>

      {/* Footer disclaimer (legal copy) */}
      <section
        style={{
          backgroundColor: brand.white,
          padding: '1.5rem 1rem 2.5rem',
          textAlign: 'center',
          borderTop: `1px solid ${brand.gray100}`,
        }}
      >
        <p
          style={{
            margin: 0,
            color: brand.gray600,
            fontSize: '0.8rem',
            lineHeight: 1.6,
            maxWidth: '720px',
            marginLeft: 'auto',
            marginRight: 'auto',
            fontStyle: 'italic',
          }}
        >
          All prices in USD. Trial period begins when you sign up for Text Message. Billing starts
          after 30 days unless cancelled before trial ends. Full terms available at{' '}
          <a
            href="/signup"
            style={{ color: brand.primary, textDecoration: 'underline', fontStyle: 'normal' }}
          >
            signup
          </a>
          .
        </p>
      </section>

      {/* Disclosure modal — shown after the user clicks "Start your free trial"
          and a phone number is on file. Handles Stripe Checkout internally. */}
      <UpgradeModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
      />
    </div>
  );
};

export default PricingPage;
